import { useState, useEffect } from 'react';
import Navigation from '../../../components/layout/Navigation';
import Footer from '../../../components/layout/Footer';
import HeroSection from './components/HeroSection';
import ApproachSection from './components/ApproachSection';
import CTASection from './components/CTASection';
import { Link } from 'react-router-dom';

export default function BrandingVisualIdentityPage() {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleSmoothScroll = (e: Event) => {
      const target = e.target as HTMLAnchorElement;
      if (target.tagName === 'A' && target.getAttribute('href')?.startsWith('#')) {
        e.preventDefault();
        const targetId = target.getAttribute('href')?.substring(1);
        const targetElement = document.getElementById(targetId || '');
        
        if (targetElement) {
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }
    };

    document.addEventListener('click', handleSmoothScroll);
    return () => document.removeEventListener('click', handleSmoothScroll);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-pink-800 text-white overflow-x-hidden">
      <Navigation />
      <main>
        <HeroSection scrollY={scrollY} />
        <ApproachSection />
        
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-300">Branding &amp; Visual Identity</span> Services
              </h2>
              <p className="text-xl text-purple-100 max-w-4xl mx-auto leading-relaxed">
                Comprehensive branding solutions that establish your unique identity and drive meaningful connections with your audience.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Link to="/services/branding/branding" className="group">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300 transform hover:scale-105 h-full">
                  <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-brain-line text-2xl text-white" />
                  </div>
                  
                  <h3 className="text-xl font-bold mb-4 text-white group-hover:text-pink-300 transition-colors duration-300">
                    Strategic Branding
                  </h3>
                  
                  <p className="text-purple-100 leading-relaxed mb-6">
                    Crafting your brand's unmistakable soul through strategic foundation, positioning, and messaging.
                  </p>
                  
                  <div className="text-pink-400 font-semibold group-hover:text-pink-300 transition-colors duration-300">
                    Learn More about Branding →
                  </div>
                </div>
              </Link>

              <Link to="/services/branding/logo-design" className="group">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300 transform hover:scale-105 h-full">
                  <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-palette-line text-2xl text-white" />
                  </div>
                  
                  <h3 className="text-xl font-bold mb-4 text-white group-hover:text-pink-300 transition-colors duration-300">
                    Logo Design
                  </h3>
                  
                  <p className="text-purple-100 leading-relaxed mb-6">
                    Creating iconic symbols that embody your brand's essence and leave lasting impressions.
                  </p>
                  
                  <div className="text-pink-400 font-semibold group-hover:text-pink-300 transition-colors duration-300">
                    Learn More about Logo Design →
                  </div>
                </div>
              </Link>

              <Link to="/services/branding/packaging-design" className="group">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300 transform hover:scale-105 h-full">
                  <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-gift-line text-2xl text-white" />
                  </div>
                  
                  <h3 className="text-xl font-bold mb-4 text-white group-hover:text-pink-300 transition-colors duration-300">
                    Packaging Design
                  </h3>
                  
                  <p className="text-purple-100 leading-relaxed mb-6">
                    Designing captivating packaging that tells your story and ensures shelf standout.
                  </p>
                  
                  <div className="text-pink-400 font-semibold group-hover:text-pink-300 transition-colors duration-300">
                    Learn More about Packaging Design →
                  </div>
                </div>
              </Link>

              <Link to="/services/branding/campaign-identity" className="group">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300 transform hover:scale-105 h-full">
                  <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-megaphone-line text-2xl text-white" />
                  </div>
                  
                  <h3 className="text-xl font-bold mb-4 text-white group-hover:text-pink-300 transition-colors duration-300">
                    Campaign Identity
                  </h3>
                  
                  <p className="text-purple-100 leading-relaxed mb-6">
                    Crafting distinct visual identities that make your campaigns unforgettable and impactful.
                  </p>
                  
                  <div className="text-pink-400 font-semibold group-hover:text-pink-300 transition-colors duration-300">
                    Learn More about Campaign Identity →
                  </div>
                </div>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-8">
              <Link to="/services/branding/graphic-motion-design" className="group lg:col-start-2">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300 transform hover:scale-105 h-full">
                  <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-movie-line text-2xl text-white" />
                  </div>
                  
                  <h3 className="text-xl font-bold mb-4 text-white group-hover:text-pink-300 transition-colors duration-300">
                    Graphic & Motion Design
                  </h3>
                  
                  <p className="text-purple-100 leading-relaxed mb-6">
                    Bringing your brand to life through stunning static visuals, engaging animations, and dynamic video content.
                  </p>
                  
                  <div className="text-pink-400 font-semibold group-hover:text-pink-300 transition-colors duration-300">
                    Learn More about Graphic & Motion Design →
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </section>

        <CTASection />
      </main>
      <Footer />
    </div>
  );
}